import { useState, useEffect, useCallback } from 'react';
import { useChainId } from 'wagmi';
import { readContract } from '@wagmi/core';
import { config } from '@/config/index';
import { getMarketplaceContract } from '@/constants/marketplace/contracts';
import { offersAbi } from '@/constants/marketplace/abi/offers';
import { type Offer } from '@chewy/typescript-config/marketplace-types';
import { type Address } from 'viem';
export interface NFTOfferInfo {
  offerId: bigint;
  offeror: Address;
  price: bigint;
  currency: Address;
  quantity: bigint;
  expirationTimestamp: bigint;
  status: number;
  isExpired: boolean;
}
export function useNFTOffers(contractAddress: string, tokenId: string) {
  const chainId = useChainId();
  const [offers, setOffers] = useState<NFTOfferInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const marketplaceContract = getMarketplaceContract(chainId);
  const marketplaceAddress = marketplaceContract?.address;
  const fetchOffersForNFT = useCallback(async () => {
    if (!marketplaceAddress || !contractAddress || !tokenId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Get total offers from marketplace
      const totalOffers = await readContract(config, {
        address: marketplaceAddress as Address,
        abi: offersAbi,
        functionName: 'totalOffers',
        chainId
      });
      const total = Number(totalOffers || 0n);
      if (total === 0) {
        setOffers([]);
        return;
      }

      // Fetch all valid offers in batches and filter for this NFT
      const batchSize = 50;
      const batches = Math.ceil(total / batchSize);
      const nftOffers: NFTOfferInfo[] = [];
      for (let i = 0; i < batches; i++) {
        const startId = BigInt(i * batchSize);
        const endId = BigInt(Math.min((i + 1) * batchSize - 1, total - 1));
        try {
          const batchOffers = await readContract(config, {
            address: marketplaceAddress as Address,
            abi: offersAbi,
            functionName: 'getAllValidOffers',
            args: [startId, endId],
            chainId
          });
          if (batchOffers) {
            const allOffers = batchOffers as Offer[];

            // Filter offers for this specific NFT
            const filteredOffers = allOffers.filter(offer => offer.assetContract.toLowerCase() === contractAddress.toLowerCase() && offer.tokenId.toString() === tokenId.toString());

            // Convert to our interface and add expiration check
            for (const offer_0 of filteredOffers) {
              const now = Math.floor(Date.now() / 1000);
              const isExpired = Number(offer_0.expirationTimestamp) <= now;
              nftOffers.push({
                offerId: offer_0.offerId,
                offeror: offer_0.offeror,
                price: offer_0.totalPrice,
                currency: offer_0.currency,
                quantity: offer_0.quantity,
                expirationTimestamp: offer_0.expirationTimestamp,
                status: offer_0.status,
                isExpired
              });
            }
          }
        } catch (batchError) {
          console.error(`Error fetching offers batch ${i}:`, batchError);
        }
      }

      // Sort by price (highest first)
      nftOffers.sort((a, b) => Number(b.price - a.price));
      setOffers(nftOffers);
    } catch (err) {
      console.error('Error fetching offers for NFT:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch offers'));
    } finally {
      setLoading(false);
    }
  }, [marketplaceAddress, contractAddress, tokenId, chainId]);
  useEffect(() => {
    if (marketplaceAddress && contractAddress && tokenId) {
      fetchOffersForNFT();
    }
  }, [fetchOffersForNFT]);
  return {
    offers,
    loading,
    error,
    refetch: fetchOffersForNFT,
    hasOffers: offers.length > 0,
    activeOffers: offers.filter(o => !o.isExpired),
    expiredOffers: offers.filter(o_0 => o_0.isExpired),
    highestOffer: offers.find(o_1 => !o_1.isExpired)
  };
}